import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {memo, type PropsWithChildren, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import type {Locale, LocaleObject} from '../../@types/Locale'
import {locales} from '../enums/locale'
import {useUserHelperGetCurrent} from '../hooks/entity/useUser'
import {useSetDayjs} from '../states/dayJs'

import {type NavigateOptions, useStableNavigate, useStableParamValue, useStablePathname, useStableSearchParam} from './StableRouter'

const getPathnameWithoutLang = (pathname: string, lang: Locale | undefined): string => {
  return `${lang ? pathname.substring(lang.length + 1) : pathname}`
}

const getUrlForLang = (pathname: string, currentLang: Locale | undefined, lang?: Locale): string => {
  const url = new URL(window.location.href)
  if (lang) {
    url.pathname = `/${lang}${getPathnameWithoutLang(pathname, currentLang)}`
  } else {
    url.pathname = getPathnameWithoutLang(pathname, currentLang)
  }

  return url.href
}

interface I18nHtmlProps {
  locale: string | undefined
}

const I18nHtml = memo(function I18nHtml({locale}: I18nHtmlProps) {
  useEffect(() => {
    if (locale) {
      document.documentElement.setAttribute('lang', locale)
    } else {
      document.documentElement.removeAttribute('lang')
    }
  }, [locale])

  return null
})

interface I18nLinkProps {
  hreflang?: Locale | 'x-default'
  href: string
  rel: 'canonical' | 'alternate'
}

const I18nLink = memo(function I18nLink({
  hreflang,
  rel,
  href,
}: I18nLinkProps) {
  useEffect(() => {
    const link = document.createElement('link')
    link.rel = rel
    link.href = href
    if (hreflang) {
      link.hreflang = hreflang
    }
    document.head.prepend(link)

    return () => {
      link.parentNode?.removeChild(link)
    }
  })

  return null
})

const LANG_QUERY_NAVIGATE_OPTIONS: NavigateOptions = {replace: true}

const getLangFromPathname = (pathname: string): Locale | undefined => {
  const segment = pathname.split('/')[1]

  return locales.find((l: LocaleObject) => l.code === segment)?.code
}

// The `lang` param lags window.location by one commit after a navigate: strip the prefix the URL actually has.
function getCurrentPathWithoutLang(): string {
  const {pathname, search, hash} = window.location

  return `${getPathnameWithoutLang(pathname, getLangFromPathname(pathname))}${search}${hash}`
}

const I18nListenRedirect = memo(function I18nListenRedirect() {
  const {i18n} = useTranslation()
  const navigate = useStableNavigate()
  const lang = useStableParamValue('lang')
  const [langQuery, setLangQuery] = useStableSearchParam('lang', LANG_QUERY_NAVIGATE_OPTIONS)

  useEffect(() => {
    if (langQuery !== undefined) {
      setLangQuery(undefined)
    }
  }, [langQuery, setLangQuery])
  useEffect(() => {
    if (lang !== i18n.resolvedLanguage) {
      navigate(`/${i18n.resolvedLanguage}${getCurrentPathWithoutLang()}`, {replace: true})
    }
  }, [i18n, lang, navigate])
  useEffect(() => {
    const changeEvent = (locale: Locale) => {
      if (locale !== getLangFromPathname(window.location.pathname)) {
        navigate(`/${locale}${getCurrentPathWithoutLang()}`)
      }
    }
    i18n.on('languageChanged', changeEvent)

    return () => {
      i18n.off('languageChanged', changeEvent)
    }
  }, [i18n, navigate])

  return null
})

const I18nListenDaysJs = memo(function I18nListenDaysJs() {
  const {i18n} = useTranslation()
  const [locale, setLocale] = useState(i18n.resolvedLanguage)
  const setDayjs = useSetDayjs()

  useEffect(() => {
    const changeEvent = (locale: Locale) => {
      setLocale(locale)
    }
    i18n.on('languageChanged', changeEvent)

    return () => {
      i18n.off('languageChanged', changeEvent)
    }
  }, [i18n])

  useEffect(() => {
    setDayjs((dayjs) => {
      dayjs.locale(locale)

      return dayjs
    })
  }, [locale, setDayjs])

  return null
})

const I18nSetLocale = memo(function I18nSetLocale() {
  const {i18n} = useTranslation()
  const {user} = useUserHelperGetCurrent()
  const userLocale = user?.locale

  useEffect(() => {
    if (userLocale) {
      i18n.changeLanguage(userLocale)
    }
  }, [i18n, userLocale])

  return null
})

const I18nLinks = memo(function I18nLinks() {
  const {i18n} = useTranslation()
  const lang = useStableParamValue('lang')
  const pathname = useStablePathname()

  return <>
    <I18nHtml locale={i18n.resolvedLanguage}/>
    {locales.map((l: LocaleObject) => <I18nLink
      key={l.code}
      rel="alternate"
      hreflang={l.code}
      href={(getUrlForLang(pathname, lang as Locale | undefined, l.code))}/>,
    )}
    <I18nLink rel="alternate" hreflang="x-default" href={(getUrlForLang(pathname, lang as Locale | undefined))}/>
  </>
})

const I18nMui = memo(function I18nMui({children}: PropsWithChildren) {
  const {i18n} = useTranslation()

  return <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.resolvedLanguage}>
    {children}
  </LocalizationProvider>
})

export const I18n = memo(function I18n({children}: PropsWithChildren) {
  return <>
    <I18nSetLocale/>
    <I18nLinks/>
    <I18nListenRedirect/>
    <I18nListenDaysJs/>
    <I18nMui>
      {children}
    </I18nMui>
  </>
})
